import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../styles/pages/articles/ArticleHome.module.scss";
import { ArticleFiltersSidebar } from "./components/Sidebar";
import { Link } from "react-router-dom";
import ArticleFooter from "./components/ArticleFooter";
// import Link from "next/link";
import ArticleHeader from "./components/ArticleHeader";
import useArticleTheme from "./components/useArticleTheme";

import { articles as DEFAULT_ARTICLES } from "../../../data/articlesIndex";
import {
  ARTICLE_CATEGORIES,
  CATEGORY_PALETTES,
  CATEGORY_KEYS,
  CATEGORY_LABEL_BY_KEY,
  ARTICLE_TOPICS
} from "./components/articleCategories";

const hrefFor = (a) => `/article/${encodeURIComponent(a.id)}`;


/** -----------------------------------------
 *  Palette n SVG cover generator (vector only)
 *  ----------------------------------------- */
function coverSVG(category = "technology", topic = "design") {
  const [c1, c2, c3] =
    CATEGORY_PALETTES[category] || CATEGORY_PALETTES.technology;

  const seed = (topic + category).length * 17;
  const y = 140 + (seed % 40);

  const svg = `
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 360'>
      <defs>
        <pattern id='p' width='18' height='18' patternUnits='userSpaceOnUse'>
          <circle cx='1' cy='1' r='1' fill='rgba(0,0,0,.08)'/>
        </pattern>
        <linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
          <stop offset='0' stop-color='${c1}'/>
          <stop offset='1' stop-color='${c2}'/>
        </linearGradient>
      </defs>
      <rect width='100%' height='100%' fill='url(#p)'/>
      <path d='M40 ${y}C220 ${y-100} 420 ${y-100} 600 ${y}' fill='none' stroke='${c1}' stroke-width='6' stroke-linecap='round'/>
      <circle cx='220' cy='${y - 100}' r='7' fill='${c1}'/>
      <circle cx='420' cy='${y - 100}' r='7' fill='${c2}'/>
      <line x1='220' y1='${y - 100}' x2='160' y2='${y - 150}' stroke='${c1}' stroke-width='2'/>
      <line x1='420' y1='${y - 100}' x2='520' y2='${y - 170}' stroke='${c2}' stroke-width='2'/>
      <circle cx='160' cy='${y - 150}' r='5' fill='${c1}'/>
      <circle cx='520' cy='${y - 170}' r='5' fill='${c2}'/>
      <g transform='translate(420,220)'>
        <polygon points='0,0 36,18 0,100 -36,18' fill='#fff3db' stroke='rgba(0,0,0,.12)'/>
        <circle cx='0' cy='40' r='6' fill='${c3}'/>
      </g>
      <rect x='80' y='60' width='120' height='140' rx='16' fill='#fff' stroke='rgba(0,0,0,.12)'/>
      <rect x='98' y='86' width='84' height='18' rx='9' fill='${c3}'/>
      <rect x='98' y='112' width='70' height='10' rx='6' fill='rgba(0,0,0,.12)'/>
      <path d='M560 60l6 12 12 6-12 6-6 12-6-12-12-6 12-6 6-12Z' fill='${c3}' opacity='.9'/>
    </svg>`;

  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
}

/** -----------------------------------------
 *  Small helpers
 *  ----------------------------------------- */
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

/** -----------------------------------------
 *  Component
 *  ----------------------------------------- */
export default function ArticleHome({


  articles = DEFAULT_ARTICLES,
  brand = "NexHer",
}) {
  // Theme toggle
  useEffect(() => {
    document.body.classList.add("articleCream");
    return () => document.body.classList.remove("articleCream");
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (saved) document.documentElement.setAttribute("data-theme", saved);
  }, []);

  // const toggleTheme = () => {
  //   const root = document.documentElement;
  //   const next = root.getAttribute("data-theme") === "dark" ? "light" : "dark";
  //   root.setAttribute("data-theme", next);
  //   localStorage.setItem("theme", next);
  // };

  const { toggleTheme } = useArticleTheme();

  // Filters (keep existing sidebar working, but we'll use category for feeds)
  const [state, setState] = useState({
  category: "all",
  topic: "all",
  tag: null,
  query: "",
  featuredOnly: false,
});

  const topics = useMemo(() => {
  // Option A: always show all predefined topics
  return ["all", ...ARTICLE_TOPICS.map((t) => t.key)];
},);



  // Using ARTICLE_CATEGORIES as the source of truth for categories
  const categories = useMemo(
    () => ["all", ...ARTICLE_CATEGORIES.map((c) => c.key)],
    []
  );


  const popularTags = useMemo(() => {
    const map = new Map();
    articles.forEach((a) =>
      (a.tags || []).forEach((t) => map.set(t, (map.get(t) || 0) + 1))
    );
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([t]) => t);
  }, [articles]);

  const getCardCover = (a) => {
  const img = (a.heroImage || "").trim();
  return img ? img : coverSVG(a.category, a.topic);
};

const filtered = useMemo(() => {
  const q = state.query.trim().toLowerCase();

  return [...articles]
    .map((a) => {
      const safeCategory = CATEGORY_KEYS.has(a.category) ? a.category : "technology";
      return { ...a, category: safeCategory };
    })
    .filter((a) => (state.category === "all" ? true : a.category === state.category))
    .filter((a) => (state.topic === "all" ? true : a.topic === state.topic))
    .filter((a) => (!state.tag ? true : (a.tags || []).includes(state.tag)))
    .filter((a) => (!state.featuredOnly ? true : !!a.featured))
    .filter((a) => {
      if (!q) return true;
      const text = (
        a.title +
        " " +
        a.summary +
        " " +
        (a.tags || []).join(" ") +
        " " +
        a.category
      ).toLowerCase();
      return text.includes(q);
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));
}, [articles, state]);





  // FEEDS: Recent can reuse, every other section is unique (no duplicates)

  const feeds = useMemo(() => {
  const sorted = filtered; // newest -> oldest

  const recent = sorted.slice(0, 3);

  // allow recent to also show in sections
  const used = new Set();

  const take = (arr, n) => {
    const out = [];
    for (const a of arr) {
      if (out.length >= n) break;
      if (used.has(a.id)) continue;
      used.add(a.id);
      out.push(a);
    }
    return out;
  };

  const byCat = (cat) => sorted.filter((a) => a.category === cat);

  const byCategory = Object.fromEntries(
    ARTICLE_CATEGORIES.map(({ key }) => [key, take(byCat(key), 12)])
  );

  const featured = take(sorted.filter((a) => !!a.featured), 8);

  return { recent, byCategory, featured };
}, [filtered]);


  const clearFilters = () =>
      setState({ category: "all", topic: "all", tag: null, query: "", featuredOnly: false });

  /** ---------- UI bits ---------- */
  const FilterButton = ({ pressed, onClick, children }) => (
    <button className={styles.smooth} aria-pressed={pressed} onClick={onClick} type="button">
      {children}
    </button>
  );

  const ArticleCard = ({ a, variant = "tile" }) => (
    <article className={styles[variant]}>
      <div
        className={
          variant === "card"
            ? styles.cardCover
            : variant === "slim"
            ? styles.slimCover
            : styles.cover
        }
      >
       {/* Article Card Navigation */}
        <Link
          to={hrefFor(a)}
          className={styles.coverLink}
          aria-label={`Open article: ${a.title}`}
          tabIndex={0}
        >
          <img
              alt={a.title}
              src={getCardCover(a)}
              loading="lazy"
              width="1280"
              height="720"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = coverSVG(a.category, a.topic);
        }}
      />
      </Link>

             {/* <img
            alt=""
            src={coverSVG(a.category, a.topic)}
            loading="lazy"
            width="1280"
            height="720"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          /> */}

        <span className={styles.badge}>
          {CATEGORY_LABEL_BY_KEY[a.category] || "Technology"}
      </span>
      </div>

      <div className={styles.content}>
        {variant === "slim" && (
          <div className={styles.pill}>
            {a.topic[0].toUpperCase() + a.topic.slice(1)} • {a.read} min
          </div>
        )}

        {/* <h3 className={styles.title}>
          <Link to={hrefFor(a)} className={styles.titleLink}>
            {a.title}
          </Link>
          <Link to={hrefFor(a)} className={styles.readMore}>
            Read more
          </Link>
        </h3>

        <p className={styles.excerpt}>{a.summary}</p> */}

        <h3 className={styles.title}>
            <Link to={hrefFor(a)} className={styles.titleLink}>
              {a.title}
            </Link>
          </h3>

          <p className={styles.excerpt}>{a.summary}</p>

          <Link to={hrefFor(a)} className={styles.readMore}>
               Read more <span aria-hidden="true">→</span>
          </Link>


        <div className={styles.meta}>
          <span className={styles.avatar}>{(a.author || "A")[0]}</span>
          <span>{formatDate(a.date)}</span>
          <span>•</span>
          <span>{a.read} min</span>
        </div>
      </div>
    </article>
  );

  const year = new Date().getFullYear();

  // Convenience groupings for your existing headings
  const techResearch = [
    ...(feeds.byCategory.technology || []),
    ...(feeds.byCategory.research || []),
  ].slice(0, 10);

  const businessCommunity = [
    ...(feeds.byCategory.business || []),
    ...(feeds.byCategory.community || []),
  ].slice(0, 8);

  const lifestyle = feeds.byCategory.lifestyle || [];

  return (
    <>
      {/* Header */}
      <ArticleHeader
          brand={brand}
          active="articles"
          query={state.query}
          onQueryChange={(val) => setState((s) => ({ ...s, query: val }))}
          onToggleTheme={toggleTheme}
          showSearch={true}
        />

      {/* Hero */}
      <section className={styles.hero}>
        <div className={`${styles.container} ${styles.heroGrid}`}>
          <div>
            <span className={styles.eyebrow}>Researchs · Blogs · Community</span>
            <h1 className={styles.heroTitle}>
            Articles on Tech, Research, and Professional Development            </h1>
            <p className={styles.heroSub}>
              Curated perspectives and analysis on innovation, progression, entrepreneurship, adventure, and public services.
            </p>

            <div className={styles.heroCtls}>
              {/* ✅ This used to be "Kinds" — now it's Categories */}
              <div className={styles.seg} role="toolbar" aria-label="Filter by category">
              {categories.map((c) => (
                <FilterButton
                  key={c}
                  pressed={state.category === c}
                  onClick={() => setState((s) => ({ ...s, category: c }))}
                >
                  {c === "all" ? "All" : CATEGORY_LABEL_BY_KEY[c]}
                </FilterButton>
              ))}
              </div>

              <div className={styles.seg} role="toolbar" aria-label="Filter by topic">
                {topics.map((t) => (
                  <FilterButton
                    key={t}
                    pressed={state.topic === t}
                    onClick={() => setState((s) => ({ ...s, topic: t }))}
                  >
                    {t[0].toUpperCase() + t.slice(1)}
                  </FilterButton>
                ))}
              </div>
            </div>
          </div>

          {/* Vector collage (unchanged) */}
          <div className={styles.heroArt} aria-hidden="true">
            <svg viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="gHero" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#6f53d5" />
                  <stop offset="1" stopColor="#14b8a6" />
                </linearGradient>
                <pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="1" cy="1" r="1" fill="rgba(0,0,0,.08)" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#dots)" />
              <path
                d="M80,420 C260,260 460,260 640,420"
                fill="none"
                stroke="url(#gHero)"
                strokeWidth="6"
                strokeLinecap="round"
              />
              <circle cx="260" cy="260" r="8" fill="#6f53d5" />
              <circle cx="460" cy="260" r="8" fill="#14b8a6" />
              <line x1="260" y1="260" x2="170" y2="210" stroke="#6f53d5" strokeWidth="2" />
              <line x1="460" y1="260" x2="560" y2="180" stroke="#14b8a6" strokeWidth="2" />
              <circle cx="170" cy="210" r="6" fill="#6f53d5" />
              <circle cx="560" cy="180" r="6" fill="#14b8a6" />
              <g transform="translate(540,260)">
                <polygon points="0,0 40,20 0,120 -40,20" fill="#fff3db" stroke="rgba(0,0,0,.12)" />
                <circle cx="0" cy="48" r="6" fill="#ff4032" />
              </g>
              <rect x="120" y="120" width="140" height="160" rx="18" fill="#fff" stroke="rgba(0,0,0,.12)" />
              <rect x="140" y="150" width="100" height="20" rx="10" fill="#ff4032" />
              <rect x="140" y="180" width="80" height="12" rx="6" fill="rgba(0,0,0,.12)" />
              <g fill="#f3b640" opacity=".9">
                <path d="M720 120l6 12 12 6-12 6-6 12-6-12-12-6 12-6 6-12Z" />
                <path d="M380 90l4 8 8 4-8 4-4 8-4-8-8-4 8-4 4-8Z" />
              </g>
            </svg>
          </div>
        </div>
      </section>

      {/* Main n Right Sidebar */}
      <main id="main" className={`${styles.container} ${styles.layout}`}>
        {/* Main Column */}
        <div>
          {/* Recent (uses feeds.recent) */}
          <section className={styles.section} aria-labelledby="mosaicHead">
            <div className={styles.sectionHead}>
              <h2 id="mosaicHead" className={styles.h2}>Recent Blogs</h2>
              <span className={styles.muted}>{Math.min(3, filtered.length)} spotlight</span>
            </div>
            <div className={styles.mosaic}>
              {feeds.recent.map((a) => (
                <ArticleCard key={`recent-${a.id}`} a={a} variant="tile" />
              ))}
            </div>
          </section>

          {/*Technology & Research */}
          <section className={styles.section} aria-labelledby="scrollHead">
            <div className={styles.sectionHead}>
              <h2 id="scrollHead" className={styles.h2}>Technology & Research</h2>
              <span className={styles.muted}>Swipe/scroll →</span>
            </div>
            <div className={`${styles.rowScroll} rowScroll`}>
              {techResearch.map((a) => (
                <ArticleCard key={`trend-${a.id}`} a={a} variant="slim" />
              ))}
            </div>
          </section>

          {/* Business & Community */}
          <section className={styles.section} aria-labelledby="featured">
            <div className={styles.sectionHead}>
              <h2 id="featured" className={styles.h2}>Business & Community</h2>
              <span className={styles.muted}>{businessCommunity.length} picks</span>
            </div>
            <div className={styles.grid}>
              {businessCommunity.map((a) => (
                <ArticleCard key={`pick-${a.id}`} a={a} variant="card" />
              ))}
            </div>
          </section>

          {/* ✅ Lifestyle (category-only, no duplicates) */}
          <section className={styles.section} aria-labelledby="latest">
            <div className={styles.sectionHead}>
              <h2 id="latest" className={styles.h2}>Lifestyle</h2>
              <span className={styles.muted}>{lifestyle.length} articles</span>
            </div>
            <div className={styles.timeline}>
              {lifestyle.map((a) => (
                <article key={`life-${a.id}`} className={styles.entry}>
                  <div className={styles.thumb}>
                    <img
                      alt=""
                      src={coverSVG(a.category, a.topic)}
                      loading="lazy"
                      width="160"
                      height="160"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </div>
                  <div>
                    <h3 className={styles.listTitle}>
                      <Link to={hrefFor(a)}>{a.title}</Link>
                    </h3>
                    <p className={styles.listSub}>{a.summary}</p>
                  </div>
                  <div className={styles.pill}>
                    {a.topic[0].toUpperCase() + a.topic.slice(1)} • {a.read} min
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        {/* Right Sidebar (kept API same, but "kinds" is now categories list) */}
        <ArticleFiltersSidebar
            categories={categories}
            topics={topics}
            popularTags={popularTags}
            selectedCategory={state.category}
            selectedTopic={state.topic}
            selectedTag={state.tag}
            featuredOnly={state.featuredOnly}
            onSelectCategory={(c) => setState((s) => ({ ...s, category: c }))}
            onSelectTopic={(t) => setState((s) => ({ ...s, topic: t }))}
            onToggleTag={(t) => setState((s) => ({ ...s, tag: s.tag === t ? null : t }))}
            onToggleFeatured={() => setState((s) => ({ ...s, featuredOnly: !s.featuredOnly }))}
            onClear={clearFilters}
        />
  </main>
      {/* Footer */}
       < ArticleFooter />

    </>
  );
}
